import { useState, useEffect } from "react";
import { getAccessToken } from "@/services/api";
import type { ApplicationStatus } from "@/types";

export type DateFieldKey = "created_at" | "applied_date" | "deadline_date" | "posting_date";

export interface DashboardFilters {
  titleFilter: string;
  companyFilter: string;
  statusFilter: ApplicationStatus | "";
  dateField: DateFieldKey;
  dateFrom: string;
  dateTo: string;
}

export const DEFAULT_FILTERS: DashboardFilters = {
  titleFilter: "",
  companyFilter: "",
  statusFilter: "",
  dateField: "created_at",
  dateFrom: "",
  dateTo: "",
};

const VALID_DATE_FIELDS: readonly DateFieldKey[] = [
  "created_at",
  "applied_date",
  "deadline_date",
  "posting_date",
];

const VALID_STATUS_VALUES = new Set(["", "saved", "applied", "interview", "offer", "rejected"]);

export function getUserIdFromToken(): string {
  const token = getAccessToken();
  if (!token) return "anonymous";
  try {
    const parts = token.split(".");
    if (parts.length < 2) return "anonymous";
    const padded = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(padded));
    return typeof payload.sub === "string" && payload.sub ? payload.sub : "anonymous";
  } catch {
    return "anonymous";
  }
}

function storageKey(userId: string): string {
  return `dashboard_filters_v1_${userId}`;
}

function isValidFilters(v: unknown): v is DashboardFilters {
  if (!v || typeof v !== "object") return false;
  const f = v as Record<string, unknown>;
  return (
    typeof f.titleFilter === "string" &&
    typeof f.companyFilter === "string" &&
    typeof f.statusFilter === "string" &&
    VALID_STATUS_VALUES.has(f.statusFilter) &&
    typeof f.dateField === "string" &&
    (VALID_DATE_FIELDS as readonly string[]).includes(f.dateField) &&
    typeof f.dateFrom === "string" &&
    typeof f.dateTo === "string"
  );
}

function loadFilters(userId: string): DashboardFilters {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return DEFAULT_FILTERS;
    const parsed: unknown = JSON.parse(raw);
    return isValidFilters(parsed) ? parsed : DEFAULT_FILTERS;
  } catch {
    return DEFAULT_FILTERS;
  }
}

export function useDashboardFilters(userId: string): {
  filters: DashboardFilters;
  setFilters: (patch: Partial<DashboardFilters>) => void;
  resetFilters: () => void;
} {
  const [filters, setFiltersState] = useState<DashboardFilters>(() =>
    loadFilters(userId)
  );

  useEffect(() => {
    localStorage.setItem(storageKey(userId), JSON.stringify(filters));
  }, [userId, filters]);

  function setFilters(patch: Partial<DashboardFilters>) {
    setFiltersState((prev) => ({ ...prev, ...patch }));
  }

  function resetFilters() {
    setFiltersState(DEFAULT_FILTERS);
  }

  return { filters, setFilters, resetFilters };
}
